import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { Scale, Plus, Clock, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function LegalServices() {
  const { API } = useAuth();
  const [services, setServices] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ service_type: "consultation", description: "", priority: "normal" });

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API}/legal-services`, { credentials: "include" });
      if (res.ok) setServices(await res.json());
    } catch (e) { console.error(e); }
  }, [API]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async () => {
    try {
      const res = await fetch(`${API}/legal-services`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      if (res.ok) { setShowForm(false); setForm({ service_type: "consultation", description: "", priority: "normal" }); fetchData(); }
    } catch (e) { console.error(e); }
  };

  const priorityConfig = {
    urgent: { color: "text-red-400", bg: "bg-red-400/10" },
    high: { color: "text-[#F59E0B]", bg: "bg-[#F59E0B]/10" },
    normal: { color: "text-[#D4AF37]", bg: "bg-[#D4AF37]/10" },
    low: { color: "text-[#556B2F]", bg: "bg-[#556B2F]/10" },
  };

  return (
    <div data-testid="legal-services-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-2xl text-white">Legal Services</h2>
          <p className="text-white/40 text-sm font-sans">{services.length} service requests</p>
        </div>
        <Button data-testid="request-legal-service-btn" onClick={() => setShowForm(true)} className="bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm font-sans">
          <Plus className="w-4 h-4 mr-2" /> Request Service
        </Button>
      </div>

      {services.length === 0 ? (
        <div className="bg-[#11130F] border border-white/10 rounded-sm p-12 text-center">
          <Scale className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-sans">No legal service requests yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {services.map((s) => {
            const pr = priorityConfig[s.priority] || priorityConfig.normal;
            return (
              <div key={s.service_id} className="bg-[#11130F] border border-white/10 rounded-sm p-5 hover:border-[#D4AF37]/40 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <Scale className="w-5 h-5 text-[#D4AF37] mt-0.5" />
                    <div>
                      <h4 className="font-sans font-medium text-white text-sm capitalize">{s.service_type.replace(/_/g, " ")}</h4>
                      {s.description && <p className="text-white/50 text-xs font-sans mt-1">{s.description}</p>}
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-white/30 text-[10px] font-sans uppercase">Status: {s.status}</span>
                        <span className={`${pr.bg} ${pr.color} text-[10px] px-2 py-0.5 rounded-sm font-sans uppercase`}>{s.priority}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-[#11130F] border border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Request Legal Service</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Service Type</label>
              <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#11130F] border-white/10">
                  <SelectItem value="consultation">Consultation</SelectItem>
                  <SelectItem value="title_verification">Title Verification</SelectItem>
                  <SelectItem value="dispute_resolution">Dispute Resolution</SelectItem>
                  <SelectItem value="documentation">Documentation</SelectItem>
                  <SelectItem value="sos_emergency">SOS Emergency</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Priority</label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#11130F] border-white/10">
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Description</label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Describe your legal service need..." className="bg-white/5 border-white/10 text-white rounded-sm" />
            </div>
            <Button data-testid="submit-legal-service-btn" onClick={handleSubmit} className="w-full bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm font-sans">Submit Request</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
